import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/hooks/use-auth";
import { signInWithGoogle, handleGoogleRedirectResult } from "@/lib/firebase";
import { useEffect, useState } from "react";

export default function AuthTestPage() {
  const { user, googleSignInMutation } = useAuth();
  const [googleUser, setGoogleUser] = useState<any>(null);
  const [authInProgress, setAuthInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Check for redirect result on page load
  useEffect(() => {
    const checkRedirect = async () => {
      try {
        setAuthInProgress(true);
        const result = await handleGoogleRedirectResult();
        
        if (result && result.user) {
          setGoogleUser({
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
          });
        }
      } catch (error: any) {
        console.error("Error checking redirect:", error);
        setErrorMessage(error.message);
      } finally {
        setAuthInProgress(false);
      }
    };
    
    checkRedirect();
  }, []);

  return (
    <div className="container max-w-3xl mx-auto py-10">
      <Card>
        <CardHeader>
          <CardTitle>Google Authentication Test</CardTitle>
          <CardDescription>
            Test Google authentication functionality in your StayMiddle app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Current user info */}
          <div className="p-4 border rounded-md">
            <h3 className="text-lg font-medium mb-2">User Status</h3>
            {user ? (
              <div className="space-y-2">
                <p className="text-green-600 font-medium">✓ Logged in to StayMiddle</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="font-medium">User ID:</div>
                  <div>{user.id}</div>
                  <div className="font-medium">Username:</div>
                  <div>{user.username}</div>
                  <div className="font-medium">Email:</div>
                  <div>{user.email}</div>
                  <div className="font-medium">Role:</div>
                  <div className="capitalize">{user.role}</div>
                  <div className="font-medium">Google ID:</div>
                  <div>{user.googleId || "Not linked with Google"}</div>
                </div>
              </div>
            ) : (
              <p className="text-yellow-600">
                Not logged in to StayMiddle. Please sign in below.
              </p>
            )}
          </div>

          {/* Google user info */}
          <div className="p-4 border rounded-md">
            <h3 className="text-lg font-medium mb-2">Google Login Status</h3>
            {googleUser ? (
              <div className="space-y-2">
                <p className="text-green-600 font-medium">✓ Google authentication successful</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="font-medium">User ID:</div>
                  <div>{googleUser.uid}</div>
                  <div className="font-medium">Email:</div>
                  <div>{googleUser.email}</div>
                  <div className="font-medium">Name:</div>
                  <div>{googleUser.displayName}</div>
                </div>
              </div>
            ) : (
              <p className="text-yellow-600">
                Not authenticated with Google. Use the button below to test Google login.
              </p>
            )}
          </div>

          {/* Error display */}
          {errorMessage && (
            <div className="p-4 border border-red-200 bg-red-50 rounded-md">
              <h3 className="text-lg font-medium text-red-700 mb-2">Error</h3>
              <p className="text-red-600">{errorMessage}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          <div className="grid grid-cols-2 gap-4 w-full">
            <Button 
              onClick={async () => {
                try {
                  setAuthInProgress(true);
                  setErrorMessage(null);
                  
                  // Direct Firebase auth
                  const result = await signInWithGoogle();
                  
                  if (result && result.user) {
                    setGoogleUser({
                      uid: result.user.uid,
                      email: result.user.email,
                      displayName: result.user.displayName,
                    });
                  }
                } catch (error: any) {
                  console.error("Error with direct Google auth:", error);
                  setErrorMessage(error.message);
                } finally {
                  setAuthInProgress(false);
                }
              }}
              disabled={authInProgress}
            >
              Test Google Sign-In
            </Button>
            
            <Button 
              onClick={() => {
                setErrorMessage(null);
                // Use our centralized auth handler
                googleSignInMutation.mutate();
              }}
              disabled={googleSignInMutation.isPending || authInProgress}
              variant="default"
            >
              Sign in to StayMiddle with Google
            </Button>
          </div>
          
          <div className="w-full">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => {
                window.location.href = "/auth";
              }}
            >
              Go to Login Page
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}